import { useCallback } from "react";

const GROUP_ORDER = [
    "basemap",
    "preview",
    "footprint",
    "imagery",
    "aoi",
    "user",       // ← user-imported / drawn layers sit above AOIs, below assets
    "asset",
    "purchase",
    "label"
];

const GROUP_ANCHORS = {
    basemap: "anchor-basemap",
    footprint: "anchor-footprint",
    imagery: "anchor-imagery",
    aoi: "anchor-aois",
    user: "anchor-user",     // ← new anchor
    asset: "anchor-assets",
    purchase: "anchor-purchase",
    label: "anchor-labels"
};

function ensureAnchor(map, id, beforeId) {
    if (map.getLayer(id)) return;

    map.addLayer(
        {
            id,
            type: "background",
            paint: { "background-opacity": 0 }
        },
        beforeId
    );
}

function getNextGroupAnchor(group) {
    const idx = GROUP_ORDER.indexOf(group);
    if (idx === -1) return null;

    const nextGroup = GROUP_ORDER[idx + 1];
    if (!nextGroup) return null;

    return GROUP_ANCHORS[nextGroup];
}

export function useLayerStackManager(mapRef) {

    const initializeAnchors = useCallback(() => {
        const map = mapRef.current;
        if (!map) return;

        GROUP_ORDER.forEach((group, idx) => {
            const anchorId = GROUP_ANCHORS[group];
            if (!anchorId) return; // groups without anchors (e.g. "preview") skip

            const nextGroup = GROUP_ORDER[idx + 1];
            const beforeId = nextGroup
                ? GROUP_ANCHORS[nextGroup]
                : undefined;

            if (!map.getLayer(anchorId)) {

                if (beforeId && map.getLayer(beforeId)) {
                    map.addLayer(
                        {
                            id: anchorId,
                            type: "background",
                            paint: { "background-opacity": 0 }
                        },
                        beforeId
                    );
                } else {
                    map.addLayer({
                        id: anchorId,
                        type: "background",
                        paint: { "background-opacity": 0 }
                    });
                }
            }
        });
    }, [mapRef]);

    const addLayer = useCallback(
        ({ group, layerConfig, beforeOverride }) => {
            const map = mapRef.current;
            if (!map) return;

            if (!GROUP_ANCHORS[group]) {
                console.warn(`Unknown layer group: ${group}`);
                return;
            }

            const beforeId = beforeOverride || getNextGroupAnchor(group);

            // Idempotent — skip if already added
            if (map.getLayer(layerConfig.id)) return;

            if (beforeId && map.getLayer(beforeId)) {
                map.addLayer(layerConfig, beforeId);
            } else {
                map.addLayer(layerConfig);
            }
        },
        [mapRef]
    );

    const removeLayer = useCallback(
        (layerId) => {
            const map = mapRef.current;
            if (!map) return;

            if (map.getLayer(layerId)) {
                map.removeLayer(layerId);
            }
        },
        [mapRef]
    );

    return {
        initializeAnchors,
        addLayer,
        removeLayer
    };
}